export interface CalculateBMIPercentileParams {
  age: number; // Age in years (2-20)
  sex: 'male' | 'female';
  bmi: number; // BMI value in kg/m²
}

export interface BMIPercentileResult {
  percentile: number;
  zScore: number;
  category: 'Underweight' | 'Healthy Weight' | 'Overweight' | 'Obese';
  categoryDescription: string;
}

// CDC LMS (Lambda-Mu-Sigma) parameters for BMI-for-age
// Comprehensive data from 2-20 years
const LMS_DATA: {
  [key in 'male' | 'female']: {
    [age: number]: {L: number; M: number; S: number};
  };
} = {
  male: {
    2.0: {L: -1.8677, M: 16.46, S: 0.08092},
    2.5: {L: -1.9567, M: 16.21, S: 0.08308},
    3.0: {L: -2.0328, M: 16.0, S: 0.0852},
    3.5: {L: -2.0957, M: 15.82, S: 0.08726},
    4.0: {L: -2.1457, M: 15.67, S: 0.08928},
    4.5: {L: -2.1833, M: 15.55, S: 0.09124},
    5.0: {L: -2.209, M: 15.46, S: 0.09315},
    5.5: {L: -2.2238, M: 15.39, S: 0.09502},
    6.0: {L: -2.2285, M: 15.34, S: 0.09683},
    6.5: {L: -2.2244, M: 15.32, S: 0.0986},
    7.0: {L: -2.2128, M: 15.33, S: 0.10033},
    7.5: {L: -2.195, M: 15.36, S: 0.10202},
    8.0: {L: -2.1722, M: 15.42, S: 0.10367},
    8.5: {L: -2.146, M: 15.5, S: 0.10529},
    9.0: {L: -2.1179, M: 15.62, S: 0.10688},
    9.5: {L: -2.0893, M: 15.76, S: 0.10844},
    10.0: {L: -2.0617, M: 15.94, S: 0.10997},
    10.5: {L: -2.0363, M: 16.14, S: 0.11148},
    11.0: {L: -2.0141, M: 16.38, S: 0.11297},
    11.5: {L: -1.996, M: 16.64, S: 0.11445},
    12.0: {L: -1.9827, M: 16.93, S: 0.11591},
    12.5: {L: -1.9747, M: 17.24, S: 0.11737},
    13.0: {L: -1.9722, M: 17.58, S: 0.11882},
    13.5: {L: -1.9751, M: 17.93, S: 0.12027},
    14.0: {L: -1.9829, M: 18.3, S: 0.12172},
    14.5: {L: -1.9948, M: 18.68, S: 0.12318},
    15.0: {L: -2.0098, M: 19.07, S: 0.12464},
    15.5: {L: -2.0269, M: 19.47, S: 0.1261},
    16.0: {L: -2.0449, M: 19.86, S: 0.12757},
    16.5: {L: -2.0625, M: 20.25, S: 0.12905},
    17.0: {L: -2.0788, M: 20.64, S: 0.13053},
    17.5: {L: -2.0929, M: 21.01, S: 0.13202},
    18.0: {L: -2.1041, M: 21.37, S: 0.13351},
    18.5: {L: -2.112, M: 21.72, S: 0.13501},
    19.0: {L: -2.1163, M: 22.05, S: 0.13651},
    19.5: {L: -2.1168, M: 22.37, S: 0.13801},
    20.0: {L: -2.1136, M: 22.67, S: 0.13952},
  },
  female: {
    2.0: {L: -1.6384, M: 16.17, S: 0.08149},
    2.5: {L: -1.7257, M: 15.94, S: 0.08413},
    3.0: {L: -1.8036, M: 15.75, S: 0.08672},
    3.5: {L: -1.872, M: 15.6, S: 0.08925},
    4.0: {L: -1.9312, M: 15.47, S: 0.09173},
    4.5: {L: -1.9815, M: 15.36, S: 0.09416},
    5.0: {L: -2.0236, M: 15.27, S: 0.09654},
    5.5: {L: -2.0582, M: 15.2, S: 0.09888},
    6.0: {L: -2.0862, M: 15.14, S: 0.10117},
    6.5: {L: -2.1086, M: 15.11, S: 0.10342},
    7.0: {L: -2.1264, M: 15.1, S: 0.10563},
    7.5: {L: -2.1408, M: 15.11, S: 0.1078},
    8.0: {L: -2.153, M: 15.15, S: 0.10994},
    8.5: {L: -2.1641, M: 15.21, S: 0.11204},
    9.0: {L: -2.175, M: 15.3, S: 0.11411},
    9.5: {L: -2.1866, M: 15.42, S: 0.11616},
    10.0: {L: -2.1995, M: 15.56, S: 0.11818},
    10.5: {L: -2.2142, M: 15.74, S: 0.12018},
    11.0: {L: -2.231, M: 15.95, S: 0.12216},
    11.5: {L: -2.25, M: 16.2, S: 0.12413},
    12.0: {L: -2.271, M: 16.48, S: 0.12608},
    12.5: {L: -2.2936, M: 16.79, S: 0.12802},
    13.0: {L: -2.3174, M: 17.13, S: 0.12995},
    13.5: {L: -2.3416, M: 17.49, S: 0.13187},
    14.0: {L: -2.3655, M: 17.86, S: 0.13378},
    14.5: {L: -2.3881, M: 18.24, S: 0.13568},
    15.0: {L: -2.4088, M: 18.62, S: 0.13757},
    15.5: {L: -2.4268, M: 19.0, S: 0.13945},
    16.0: {L: -2.4417, M: 19.37, S: 0.14132},
    16.5: {L: -2.4533, M: 19.73, S: 0.14318},
    17.0: {L: -2.4616, M: 20.08, S: 0.14503},
    17.5: {L: -2.4667, M: 20.41, S: 0.14687},
    18.0: {L: -2.4689, M: 20.72, S: 0.1487},
    18.5: {L: -2.4685, M: 21.01, S: 0.15052},
    19.0: {L: -2.4658, M: 21.29, S: 0.15232},
    19.5: {L: -2.4614, M: 21.54, S: 0.15412},
    20.0: {L: -2.4557, M: 21.78, S: 0.1559},
  },
};

/**
 * Calculates BMI percentile for children and adolescents (ages 2-20) using CDC growth charts
 * Based on the LMS (Lambda-Mu-Sigma) method
 *
 * @param data - Object containing age, sex, and BMI
 * @returns BMIPercentileResult with percentile, z-score, and category
 */
export const calculateBMIPercentile = (data: CalculateBMIPercentileParams): BMIPercentileResult => {
  const {age, sex, bmi} = data;

  // Validate inputs
  if (age < 2 || age > 20) {
    throw new Error('Age must be between 2 and 20 years');
  }
  if (bmi <= 0) {
    throw new Error('BMI must be greater than 0');
  }

  // Get LMS parameters for the given age and sex
  const lmsParams = getLMSParameters(age, sex);
  const {L, M, S} = lmsParams;

  // Calculate Z-score using the LMS method
  // Z = [(BMI/M)^L - 1] / (L × S)
  const zScore = L !== 0 ? (Math.pow(bmi / M, L) - 1) / (L * S) : Math.log(bmi / M) / S; // Special case when L = 0

  // Convert Z-score to percentile
  const percentile = zScoreToPercentile(zScore);

  // Determine category based on percentile
  const category = getCategory(percentile);
  const categoryDescription = getCategoryDescription(category);

  return {
    percentile: Math.round(percentile * 10) / 10, // Round to 1 decimal
    zScore: Math.round(zScore * 100) / 100, // Round to 2 decimals
    category,
    categoryDescription,
  };
};

/**
 * Gets LMS parameters for a given age and sex, with interpolation for fractional ages
 */
function getLMSParameters(age: number, sex: 'male' | 'female'): {L: number; M: number; S: number} {
  const sexData = LMS_DATA[sex];
  const ages = Object.keys(sexData)
    .map(Number)
    .sort((a, b) => a - b);

  // Find exact match or interpolate
  if (sexData[age]) {
    return sexData[age];
  }

  // Find surrounding ages for interpolation
  let lowerAge = ages[0];
  let upperAge = ages[ages.length - 1];

  for (let i = 0; i < ages.length - 1; i++) {
    if (ages[i] <= age && ages[i + 1] > age) {
      lowerAge = ages[i];
      upperAge = ages[i + 1];
      break;
    }
  }

  // Linear interpolation
  const lowerParams = sexData[lowerAge];
  const upperParams = sexData[upperAge];
  const fraction = (age - lowerAge) / (upperAge - lowerAge);

  return {
    L: lowerParams.L + (upperParams.L - lowerParams.L) * fraction,
    M: lowerParams.M + (upperParams.M - lowerParams.M) * fraction,
    S: lowerParams.S + (upperParams.S - lowerParams.S) * fraction,
  };
}

/**
 * Converts a Z-score to percentile using the cumulative distribution function
 * of the standard normal distribution
 */
function zScoreToPercentile(z: number): number {
  // Using the error function approximation for cumulative normal distribution
  const sign = z >= 0 ? 1 : -1;
  const x = Math.abs(z) / Math.sqrt(2);

  // Error function approximation
  const t = 1 / (1 + 0.3275911 * x);
  const erf =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);

  // Convert to percentile
  const percentile = 50 * (1 + sign * erf);

  // Clamp to valid range
  return Math.max(0, Math.min(100, percentile));
}

/**
 * Determines the weight category based on BMI percentile
 */
function getCategory(percentile: number): 'Underweight' | 'Healthy Weight' | 'Overweight' | 'Obese' {
  if (percentile < 5) return 'Underweight';
  if (percentile < 85) return 'Healthy Weight';
  if (percentile < 95) return 'Overweight';
  return 'Obese';
}

/**
 * Gets a description for the weight category
 */
function getCategoryDescription(category: 'Underweight' | 'Healthy Weight' | 'Overweight' | 'Obese'): string {
  const descriptions = {
    Underweight: 'BMI is below the 5th percentile. Consider nutritional assessment.',
    'Healthy Weight': 'BMI is within the healthy range (5th to 85th percentile).',
    Overweight: 'BMI is between 85th and 95th percentile. Consider lifestyle modifications.',
    Obese: 'BMI is at or above the 95th percentile. Medical evaluation recommended.',
  };

  return descriptions[category];
}
